import {
  useRequestDocumentUploadUrl,
  useReprocessDocument,
} from "@elation/api-client/src/stitch-web";
import type { UploadUrlRequestMimeType } from "@elation/api-client/src/stitch-web/models";
import { Text } from "@src/components/general/Text";
import { Flexbox, VerticalFlexbox } from "@src/components/layout/Flexbox";
import { cn } from "@src/lib/utils";
import { useMutation } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import { useRef, useState } from "react";
import { JobCard } from "./JobCard";
import type { DocumentJob } from "./useDocumentJobs";

interface ImportPanelProps {
  patientId: number;
  jobs: DocumentJob[];
  onJobAdded: (jobId: string, filename: string, fileSize?: number) => void;
  onJobFailed: (jobId: string) => void;
  onJobComplete: (jobId: string) => void;
  onJobRemove: (jobId: string) => void;
}

export const ImportPanel = ({
  patientId,
  jobs,
  onJobAdded,
  onJobFailed,
  onJobComplete,
  onJobRemove,
}: ImportPanelProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutateAsync: requestUploadUrl } = useRequestDocumentUploadUrl();
  const { mutate: reprocess } = useReprocessDocument();

  const { mutateAsync: uploadToS3 } = useMutation({
    mutationFn: async ({ url, file }: { url: string; file: File }) => {
      const response = await fetch(url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!response.ok) {
        throw new Error(`S3 upload failed with status ${response.status}`);
      }
    },
  });

  const handleFiles = (files: FileList | null) => {
    if (!files?.length) return;
    void Promise.all(
      Array.from(files).map(async (file) => {
        let jobId: string | undefined;
        try {
          const { data } = await requestUploadUrl({
            data: {
              fileName: file.name,
              mimeType: file.type as UploadUrlRequestMimeType,
              patientId,
            },
          });
          jobId = data.jobId;
          if (jobId) onJobAdded(jobId, file.name, file.size);
          await uploadToS3({ url: data.uploadUrl, file });
          reprocess({ id: data.documentId });
        } catch (err) {
          console.error("[ImportPanel] Upload failed", {
            fileName: file.name,
            err,
          });
          if (jobId) {
            onJobFailed(jobId);
          }
        }
      }),
    );
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <VerticalFlexbox gap="sm">
      <button
        type="button"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        aria-label="Upload document"
        aria-describedby="upload-document-hint"
        className={cn(
          "flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-muted/30 px-4 py-8 text-center transition-all",
          "hover:border-accent/60 hover:bg-accent/5",
          isDragging && "border-accent bg-accent/10",
        )}
      >
        <Flexbox
          alignItems="center"
          justifyContent="center"
          className="size-10 rounded-full bg-accent/10 text-accent"
        >
          <Upload className="size-5" />
        </Flexbox>
        <Text sizing="sm" fontWeight={500}>
          Drag &amp; drop files here
        </Text>
        <Text sizing="xs" appearance="subdued" id="upload-document-hint">
          PDF, PNG, JPG supported
        </Text>
        <Text
          as="span"
          sizing="xs"
          fontWeight={600}
          className="mt-1 rounded-md bg-background px-3 py-1 text-accent shadow-sm"
        >
          Browse files
        </Text>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg"
          multiple
          className="hidden"
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </button>

      {jobs.length > 0 ? (
        <VerticalFlexbox gap="xs">
          {jobs.map((job) => (
            <JobCard
              key={job.jobId}
              job={job}
              onComplete={onJobComplete}
              onRemove={onJobRemove}
            />
          ))}
        </VerticalFlexbox>
      ) : null}
    </VerticalFlexbox>
  );
};
